import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { BotaoLixeira } from '../../componentes/lixeira';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { cabecalhoEmpresa, del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { dinheiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { invalidarAssinaturas, useOpcoesContrato } from './dados';
import { textoRecorrencia } from './DetalheContrato';
import { atalhoDoContrato, recorrenciaDaTela } from './FormContrato';
import { ItensContrato, itensParaApi, novoItem, quantidadeDe, type ItemForm } from './ItensContrato';
import { CAMINHO_PLANOS, INTERVALOS, TIPOS_CONTRATO, totalDaLinha, UNIDADES, type Plano, type PlanoResumo } from './tipos';

const itemDoPlano = (i: Plano['itens'][number]): ItemForm => ({
  chave: crypto.randomUUID(),
  servico_id: i.servico_id ?? '',
  descricao: i.descricao,
  quantidade: String(i.quantidade).replace('.', ','),
  preco: i.preco_centavos,
  custo: null,
  categoria_id: '',
  centro_custo_id: '',
  recorrencia: '',
});

/** Formulário do plano: nome, descrição, ativo, recorrência (como a do contrato) e os itens (serviços do Cadastros). */
function FormPlano({ plano, idForm, aoSalvar }: { plano: Plano | null; idForm: string; aoSalvar: () => void }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const opcoes = useOpcoesContrato();
  const [novoId] = useState(() => crypto.randomUUID());
  const [itens, setItens] = useState<ItemForm[]>(plano ? plano.itens.map(itemDoPlano) : [novoItem()]);
  const podeEditar = plano ? s.pode('assinaturas.editar') : s.pode('assinaturas.criar');
  const moeda = plano?.moeda ?? s.workspace.moeda;
  const recorrente = (v: Record<string, unknown>) => v.tipo !== 'avulso';
  const campos: DefCampo[] = [
    // V2 verticais: plano novo sem empresa na aba (sem filtro, várias permitidas) pede a empresa.
    ...(plano ? [] : campoEmpresaDoRegistro(s.empresas, { rotulo: t('campos.empresa'), ajuda: tn('empresa.ajudaRegistro') })),
    { nome: 'nome', rotulo: t('planos.campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia', desabilitado: !podeEditar },
    { nome: 'ativo', rotulo: t('planos.campos.ativo'), tipo: 'booleano', largura: 'meia', ajuda: t('planos.ajuda.ativo'), desabilitado: !podeEditar },
    { nome: 'descricao', rotulo: t('planos.campos.descricao'), tipo: 'area', desabilitado: !podeEditar },
    { nome: 'tipo', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, largura: 'meia', desabilitado: !podeEditar, opcoes: TIPOS_CONTRATO.map((x) => ({ valor: x, rotulo: t(`tipos.${x}`) })) },
    {
      nome: 'intervalo',
      rotulo: t('campos.repete'),
      tipo: 'selecao',
      largura: 'meia',
      visivel: recorrente,
      desabilitado: !podeEditar,
      opcoes: [...INTERVALOS.map((n) => ({ valor: String(n), rotulo: t(`intervalos.${n}`) })), { valor: 'semanal', rotulo: t('intervalos.semanal') }, { valor: 'outro', rotulo: t('intervalos.outro') }],
    },
    { nome: 'intervalo_meses', rotulo: t('campos.intervaloMeses'), tipo: 'numero', largura: 'meia', min: 1, max: 60, passo: 1, visivel: (v) => recorrente(v) && v.intervalo === 'outro', desabilitado: !podeEditar },
    {
      nome: 'unidade_recorrencia',
      rotulo: t('campos.unidade'),
      tipo: 'selecao',
      largura: 'meia',
      visivel: (v) => recorrente(v) && v.intervalo === 'outro',
      desabilitado: !podeEditar,
      opcoes: UNIDADES.map((x) => ({ valor: x, rotulo: t(`unidades.${x}`) })),
    },
  ];
  const inicial: Record<string, unknown> = plano
    ? { nome: plano.nome, ativo: plano.ativo, descricao: plano.descricao ?? '', tipo: plano.tipo, intervalo: atalhoDoContrato(plano), intervalo_meses: plano.intervalo_meses, unidade_recorrencia: plano.unidade_recorrencia }
    : { ativo: true, tipo: 'recorrente', intervalo: '1', intervalo_meses: 1, unidade_recorrencia: 'mes' };
  const preco = itens.reduce((soma, i) => soma + totalDaLinha(quantidadeDe(i.quantidade), i.preco ?? 0), 0);
  return (
    <Formulario
      id={idForm}
      campos={campos}
      entidade="assinaturas.plano"
      inicial={inicial}
      aoEnviar={async (f) => {
        const rec = recorrenciaDaTela(f);
        const corpo = {
          nome: String(f.nome ?? '').trim(),
          descricao: String(f.descricao ?? '').trim() || null,
          ativo: !!f.ativo,
          tipo: f.tipo,
          intervalo_meses: rec.intervalo,
          unidade_recorrencia: rec.unidade,
          itens: itensParaApi(itens, t('itens.incompleto')).map(({ servico_id, descricao, quantidade, preco_centavos }) => ({ servico_id, descricao, quantidade, preco_centavos })),
        };
        if (plano) await patch(`${CAMINHO_PLANOS}/${plano.id}`, corpo);
        else await post(CAMINHO_PLANOS, { id: novoId, ...corpo }, { 'Idempotency-Key': novoId, ...cabecalhoEmpresa(empresaEscolhida(f)) });
        await invalidarAssinaturas(qc);
        toast.sucesso(tn('comum.salvo'));
        aoSalvar();
      }}
    >
      <div className="flex min-w-0 flex-col gap-3 sm:col-span-6">
        <h3 className="text-sm font-semibold text-tinta-1">{t('planos.itens')}</h3>
        <ItensContrato itens={itens} aoMudar={setItens} servicos={opcoes.data?.servicos ?? []} categorias={null} centros={null} moeda={moeda} desabilitado={!podeEditar} semCusto />
        <p className="text-sm text-tinta-2">
          {t('planos.preco')}: <span className="numero font-semibold text-tinta-1">{anunciarOcultos(dinheiro(preco, moeda))}</span>
        </p>
      </div>
    </Formulario>
  );
}

/** Drawer do plano: o novo abre no formulário; o salvo carrega o detalhe (itens) e abre no formulário com Excluir. */
function DrawerPlano({ alvo, aoFechar }: { alvo: string | 'novo'; aoFechar: () => void }) {
  const { t } = useTranslation('assinaturas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [excluindo, setExcluindo] = useState(false);
  const id = alvo === 'novo' ? null : alvo;
  const q = useQuery({ queryKey: [CAMINHO_PLANOS, 'detalhe', id], queryFn: () => get<Plano>(`${CAMINHO_PLANOS}/${id}`), enabled: !!id });
  const excluir = useMutation({
    mutationFn: () => del(`${CAMINHO_PLANOS}/${id}`),
    onSuccess: async () => {
      setExcluindo(false);
      await invalidarAssinaturas(qc);
      toast.sucesso(tn('comum.removido'));
      aoFechar();
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });
  const idForm = `form-plano-${id ?? 'novo'}`;
  const podeSalvar = id ? s.pode('assinaturas.editar') : s.pode('assinaturas.criar');
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={id ? (q.data?.nome ?? t('abas.planos')) : t('planos.novo')}
      rodape={
        <>
          {id && q.data && s.pode('assinaturas.excluir') && (
            <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
              {tn('comum.excluir')}
            </Botao>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          {podeSalvar && (
            <Botao variante="primario" type="submit" form={idForm}>
              {tn('comum.salvar')}
            </Botao>
          )}
        </>
      }
    >
      {!id ? (
        <FormPlano plano={null} idForm={idForm} aoSalvar={aoFechar} />
      ) : q.isLoading ? (
        <Carregando />
      ) : q.isError || !q.data ? (
        <Erro erro={q.error} aoTentar={() => void q.refetch()} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {q.data.contratos_ativos > 0 && <p className="text-sm text-tinta-2">{t('planos.contratosAtivos', { count: q.data.contratos_ativos })}</p>}
          <FormPlano key={q.data.id} plano={q.data} idForm={idForm} aoSalvar={aoFechar} />
        </div>
      )}
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('planos.excluirTitulo')} texto={t('planos.excluirTexto')} />
    </Drawer>
  );
}

/** Pacote 2 (onda 1a): aba Planos, o catálogo de planos de assinatura (spec: Plano = nome, preço, periodicidade, itens). */
export function Planos() {
  const { t } = useTranslation('assinaturas');
  const s = useSessao();
  const [aberto, setAberto] = useState<string | 'novo' | null>(null);
  const [ativo, setAtivo] = useState('');
  const colunas: Coluna<PlanoResumo>[] = [
    {
      chave: 'nome',
      rotulo: t('planos.campos.nome'),
      principal: true,
      render: (p) => (
        <span className="flex min-w-0 flex-col">
          <span className="font-medium">{p.nome}</span>
          {p.descricao && (
            <span className="truncate text-xs text-tinta-3" title={textoLivre(p.descricao)}>
              {textoLivre(p.descricao)}
            </span>
          )}
        </span>
      ),
    },
    { chave: 'intervalo_meses', rotulo: t('campos.intervalo'), render: (p) => textoRecorrencia(t, p) },
    { chave: 'preco_centavos', rotulo: t('planos.preco'), alinhar: 'direita', mono: true, render: (p) => dinheiro(p.preco_centavos, p.moeda) },
    { chave: 'n_itens', rotulo: t('planos.campos.itens'), alinhar: 'direita', mono: true },
    { chave: 'contratos_ativos', rotulo: t('planos.campos.contratos'), alinhar: 'direita', mono: true },
    { chave: 'ativo', rotulo: t('planos.campos.ativo'), render: (p) => <Selo cor={p.ativo ? 'verde' : 'neutro'}>{p.ativo ? t('planos.ativo') : t('planos.inativo')}</Selo> },
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {(s.pode('assinaturas.criar') || s.pode('assinaturas.excluir')) && (
        <AcaoPrimaria>
          {s.pode('assinaturas.excluir') && <BotaoLixeira entidades={['assinaturas.plano']} />}
          {s.pode('assinaturas.criar') && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
              {t('planos.novo')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<PlanoResumo>
        tela="assinaturas.planos"
        caminho={CAMINHO_PLANOS}
        params={{ ativo: ativo || undefined }}
        filtros={{ ativo }}
        aoAplicarFiltros={(f) => setAtivo(String(f.ativo ?? ''))}
        colunas={colunas}
        entidade="assinaturas.plano"
        aoClicar={(p) => setAberto(p.id)}
        vazio={<Vazio texto={t('planos.vazio')} />}
        filtrosExtra={
          <div className="w-full sm:w-48">
            <Selecao
              aria-label={t('planos.filtroAtivo')}
              vazio={t('planos.todos')}
              opcoes={[
                { valor: 'true', rotulo: t('planos.ativos') },
                { valor: 'false', rotulo: t('planos.inativos') },
              ]}
              value={ativo}
              onChange={(e) => setAtivo(e.target.value)}
            />
          </div>
        }
      />
      {aberto && <DrawerPlano key={aberto} alvo={aberto} aoFechar={() => setAberto(null)} />}
    </div>
  );
}
